import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Input,
  Progress,
  Row,
} from "reactstrap";
import { MOCK_WORKERS } from "./mockWorkers";
import WorkerCandidateCard from "./WorkerCandidateCard";
import AssignWorkerModal from "./AssignWorkerModal";
import {
  buildShiftRequirements,
  filterAndRankCandidates,
  getDraftAssignedDates,
  getPatternRoleTitle,
  getShiftFillSummary,
  labelDbsType,
  labelWorkForce,
  removeDraftAssignment,
  upsertDraftAssignment,
} from "./workerAssignmentHelpers";

function WorkerAssignmentPanel({ booking }) {
  const patterns = useMemo(
    () => (Array.isArray(booking?.shiftPattern) ? booking.shiftPattern : []),
    [booking?.shiftPattern]
  );

  const [selectedShiftIndex, setSelectedShiftIndex] = useState(0);
  const [search, setSearch] = useState("");
  const [draftAssignments, setDraftAssignments] = useState([]);
  const [assignWorker, setAssignWorker] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const safeShiftIndex =
    patterns.length === 0
      ? 0
      : Math.min(selectedShiftIndex, patterns.length - 1);
  const selectedPattern = patterns[safeShiftIndex] || null;

  const requirements = useMemo(
    () => (selectedPattern ? buildShiftRequirements(selectedPattern) : null),
    [selectedPattern]
  );

  const fillSummary = useMemo(() => {
    if (!selectedPattern) {
      return {
        datesCount: 0,
        quantity: 0,
        totalSlots: 0,
        filledSlots: 0,
        remainingSlots: 0,
        percent: 0,
      };
    }
    return getShiftFillSummary(
      selectedPattern,
      safeShiftIndex,
      draftAssignments
    );
  }, [selectedPattern, safeShiftIndex, draftAssignments]);

  const candidates = useMemo(() => {
    if (!selectedPattern) return [];
    return filterAndRankCandidates({
      workers: MOCK_WORKERS,
      pattern: selectedPattern,
      search,
    });
  }, [selectedPattern, search]);

  const assignedForShift = useMemo(
    () =>
      (draftAssignments || []).filter((a) => a.shiftIndex === safeShiftIndex),
    [draftAssignments, safeShiftIndex]
  );

  const openAssignModal = (worker) => {
    setAssignWorker(worker);
    setModalOpen(true);
  };

  const handleConfirmDates = (dates) => {
    if (!assignWorker) return;
    setDraftAssignments((prev) =>
      upsertDraftAssignment(prev, {
        shiftIndex: safeShiftIndex,
        workerSlug: assignWorker.slug,
        dates,
      })
    );
  };

  if (!patterns.length) {
    return (
      <Card className="mb-3">
        <CardBody className="text-muted text-center py-4">
          No shift patterns available for worker assignment.
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="mb-3 br-wa-panel">
      <CardHeader className="bg-primary text-white">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
          <h5 className="mb-0">Assign Workers</h5>
          <Button
            color="light"
            size="sm"
            onClick={() => setDraftAssignments([])}
            disabled={draftAssignments.length === 0}
          >
            Clear all
          </Button>
        </div>
      </CardHeader>

      <CardBody>
        <div className="br-wa-shift-rail mb-3">
          {patterns.map((pattern, index) => {
            const summary = getShiftFillSummary(
              pattern,
              index,
              draftAssignments
            );
            const active = index === safeShiftIndex;
            return (
              <button
                key={index}
                type="button"
                className={`br-wa-shift-chip ${
                  active ? "br-wa-shift-chip--active" : ""
                }`}
                onClick={() => setSelectedShiftIndex(index)}
              >
                <div className="br-wa-shift-chip__title">
                  Shift {index + 1} — {getPatternRoleTitle(pattern)}
                </div>
                <div className="br-wa-shift-chip__meta">
                  {pattern.startTime || "—"} – {pattern.endTime || "—"} · Qty{" "}
                  {pattern.quantity || 0} · {summary.datesCount} date
                  {summary.datesCount !== 1 ? "s" : ""}
                </div>
                <Progress
                  value={summary.percent}
                  className="br-wa-shift-chip__progress"
                  color={summary.percent >= 100 ? "success" : "primary"}
                />
                <div className="br-wa-shift-chip__fill">
                  {summary.filledSlots}/{summary.totalSlots} slots filled
                </div>
              </button>
            );
          })}
        </div>

        {requirements && (
          <Card className="border mb-3">
            <CardHeader className="bg-light">
              <h6 className="mb-0">Shift requirements</h6>
            </CardHeader>
            <CardBody>
              <Row className="gy-3">
                <Col md={4}>
                  <small className="text-muted d-block">Role</small>
                  <div className="fw-medium">{requirements.roleTitle}</div>
                </Col>
                <Col md={4}>
                  <small className="text-muted d-block">Shift time</small>
                  <div className="fw-medium">
                    {requirements.startTime} – {requirements.endTime}
                  </div>
                </Col>
                <Col md={4}>
                  <small className="text-muted d-block">Workers / date</small>
                  <div className="fw-medium">{requirements.quantity}</div>
                </Col>
                <Col md={4}>
                  <small className="text-muted d-block">Experience</small>
                  <div className="fw-medium">
                    {requirements.experienceLevels.length
                      ? requirements.experienceLevels.join(", ")
                      : "Any"}
                  </div>
                </Col>
                <Col md={4}>
                  <small className="text-muted d-block">Clearances</small>
                  <div className="fw-medium">
                    {requirements.certSlugs.length
                      ? requirements.certSlugs.join(", ")
                      : "None specified"}
                  </div>
                </Col>
                <Col md={4}>
                  <small className="text-muted d-block">DBS</small>
                  <div className="fw-medium">
                    {requirements.dbsType
                      ? `${labelDbsType(requirements.dbsType)}${
                          requirements.workForce
                            ? ` · ${labelWorkForce(requirements.workForce)}`
                            : ""
                        }`
                      : "Not required"}
                  </div>
                </Col>
              </Row>
              <div className="mt-3">
                <div className="d-flex justify-content-between small text-muted mb-1">
                  <span>Shift fill</span>
                  <span>
                    {fillSummary.filledSlots}/{fillSummary.totalSlots}
                  </span>
                </div>
                <Progress
                  value={fillSummary.percent}
                  color={fillSummary.percent >= 100 ? "success" : "primary"}
                />
              </div>
            </CardBody>
          </Card>
        )}

        <Row className="g-3">
          <Col lg={8}>
            <Row className="g-2 mb-3">
              <Col md={12}>
                <Input
                  type="search"
                  placeholder="Search eligible workers by name, clearance, or location"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </Col>
            </Row>

            {candidates.length === 0 ? (
              <div className="text-center text-muted py-5 border rounded bg-light">
                <i className="mdi mdi-account-search-outline br-wa-empty__icon" />
                <p className="mb-0 mt-2">
                  No eligible workers match this search for the selected shift.
                </p>
              </div>
            ) : (
              <div className="br-wa-candidate-grid">
                {candidates.map((candidate) => {
                  const assignedDates = getDraftAssignedDates(
                    draftAssignments,
                    safeShiftIndex,
                    candidate.worker.slug
                  );
                  return (
                    <WorkerCandidateCard
                      key={candidate.worker.slug}
                      candidate={candidate}
                      assignedDatesCount={assignedDates.length}
                      onAssign={openAssignModal}
                    />
                  );
                })}
              </div>
            )}
          </Col>

          <Col lg={4}>
            <Card className="border br-wa-assigned-panel">
              <CardHeader className="bg-light">
                <h6 className="mb-0">Assigned workers</h6>
              </CardHeader>
              <CardBody>
                <p className="text-muted small mb-3">
                  Shift {safeShiftIndex + 1} — grouped by date
                </p>

                {assignedForShift.length === 0 ? (
                  <div className="text-muted small py-3 text-center border rounded bg-light">
                    No workers assigned to this shift yet.
                  </div>
                ) : (
                  <div className="br-wa-assigned-list">
                    {(requirements?.dates || []).map((dateIso) => {
                      const rows = assignedForShift.filter((a) =>
                        (a.dates || []).includes(dateIso)
                      );
                      if (!rows.length) return null;
                      return (
                        <div key={dateIso} className="br-wa-assigned-day">
                          <div className="br-wa-assigned-day__title">
                            {dayjs(dateIso).format("ddd, DD MMM YYYY")}
                            <Badge className="bg-primary">
                              {rows.length}
                            </Badge>
                          </div>
                          <ul className="br-wa-assigned-day__workers">
                            {rows.map((row) => {
                              const worker = MOCK_WORKERS.find(
                                (w) => w.slug === row.workerSlug
                              );
                              return (
                                <li key={`${row.workerSlug}-${dateIso}`}>
                                  <span>
                                    {worker?.fullName || row.workerSlug}
                                  </span>
                                  <Button
                                    color="link"
                                    size="sm"
                                    className="text-danger p-0"
                                    onClick={() =>
                                      setDraftAssignments((prev) =>
                                        removeDraftAssignment(
                                          prev,
                                          safeShiftIndex,
                                          row.workerSlug,
                                          dateIso
                                        )
                                      )
                                    }
                                  >
                                    Remove
                                  </Button>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </CardBody>

      <AssignWorkerModal
        isOpen={modalOpen}
        toggle={() => {
          setModalOpen(false);
          setAssignWorker(null);
        }}
        worker={assignWorker}
        pattern={selectedPattern}
        shiftIndex={safeShiftIndex}
        draftAssignments={draftAssignments}
        onConfirm={handleConfirmDates}
      />
    </Card>
  );
}

WorkerAssignmentPanel.propTypes = {
  booking: PropTypes.object,
};

WorkerAssignmentPanel.defaultProps = {
  booking: null,
};

export default WorkerAssignmentPanel;
